import { apiAxios } from '@/api/axios';
import type {
  PlaceCreate,
  PlaceDto,
  PlacePage,
  PlaceSummary,
  PlaceType,
  PlaceUpdate,
} from '@/api/types/Place';

// Adresář service — wraps `/api/places/*` (CHR-06 contract §C). Functional,
// one export per endpoint; mirrors `registryService.ts` (autocomplete) and the
// offset-paged `notificationsService.ts`.
//
// Offset paging: `page` is 0-based (Spring native — maps DIRECTLY to `?page=`
// with NO off-by-one translation), default `size` 50, clamped server-side to
// [1, 200]. Rows arrive sorted `LOWER(name) ASC, id ASC` inside the
// `OffsetPageResponse` envelope (ADR-0044); there is no `sort` param in v1.
//
// Roles: read = backoffice ∨ dispatcher ∨ viewer; POST/PUT/DELETE = backoffice
// ∨ dispatcher. The FE hides the affordances it may not use; the api enforces
// (403).

export const PLACES_PAGE_SIZE = 50;

export interface ListPlacesParams {
  // Free-text needle matched server-side over name ∨ address ∨ contact ∨ note.
  // ⚠ The param is `query` — NOT `q` (repo-wide convention).
  query?: string;
  // Undefined ⇒ „Vše" (no type filter — every live row). The three chips are
  // NOT symmetric (user 2026-09-17: „Obojí a Vše vlastně funguje stejně"):
  //   `LOADING`   ⇒ `IN (LOADING, BOTH)`   — a „Obojí" place IS a nakládka,
  //   `UNLOADING` ⇒ `IN (UNLOADING, BOTH)` — …and IS a vykládka,
  //   `BOTH`      ⇒ `= BOTH` EXACTLY       — „která místa umí obojí?".
  // An inclusive `BOTH` would have returned the whole registry, making the
  // chip a duplicate of „Vše".
  type?: PlaceType;
  page?: number;
  size?: number;
}

export const listPlaces = async ({
  query,
  type,
  page = 0,
  size = PLACES_PAGE_SIZE,
}: ListPlacesParams = {}): Promise<PlacePage> => {
  const response = await apiAxios.get<PlacePage>('/api/places', {
    params: {
      page,
      size,
      // Omit empty filters entirely — an empty `query=` would be a distinct
      // (blank-needle) request on the api side.
      ...(query ? { query } : {}),
      ...(type ? { type } : {}),
    },
  });
  return response.data;
};

export const getPlace = async (id: string): Promise<PlaceDto> => {
  const response = await apiAxios.get<PlaceDto>(`/api/places/${id}`);
  return response.data;
};

export const createPlace = async (payload: PlaceCreate): Promise<PlaceDto> => {
  const response = await apiAxios.post<PlaceDto>('/api/places', payload);
  return response.data;
};

export const updatePlace = async (
  id: string,
  payload: PlaceUpdate,
): Promise<PlaceDto> => {
  const response = await apiAxios.put<PlaceDto>(`/api/places/${id}`, payload);
  return response.data;
};

// Soft-delete. `version` rides as a query param (not a body) — DELETE bodies
// are not part of the contract; a stale version answers 409 OPTIMISTIC_LOCK.
export const deletePlace = async (id: string, version: number): Promise<void> => {
  await apiAxios.delete(`/api/places/${id}`, { params: { version } });
};

// Autocomplete source for the jízda editor (W2). Returns a BARE array capped
// server-side at 20 rows, soft-deleted places excluded. `type` filters
// INCLUSIVELY of `BOTH`, so the Nakládka field also offers „Obojí" places.
export const searchPlaces = async (
  query: string,
  type: 'LOADING' | 'UNLOADING',
): Promise<PlaceSummary[]> => {
  const response = await apiAxios.get<PlaceSummary[]>('/api/places/autocomplete', {
    params: {
      ...(query ? { query } : {}),
      type,
    },
  });
  return response.data;
};
